// src/pages/dropshipping/DropshippingInvoiceDetailPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileDown, Download, Search, X } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { getDropshippingInvoiceById } from '../../services/api/dropshipping';
import { formatDate } from '../../utils/dateUtils';
import styles from './DropshippingInvoiceDetailPage.module.css';

// ── helpers ─────────────────────────────────────────────────────────────────
const fmt = (val) => (val == null ? '-' : Number(val).toFixed(2));

const STATUS_CSS = {
  generated: 'status_generated',
  paid: 'status_paid',
  disputed: 'status_disputed',
};

const SUMMARY_ITEMS = [
  { label: 'totalOrders',             key: 'totalOrders',             currency: false },
  { label: 'totalDelivered',          key: 'totalDeliveredOrders',    currency: false },
  { label: 'totalReturned',           key: 'totalReturnedOrders',     currency: false },
  { label: 'totalCodCollected',       key: 'totalCodCollected',       currency: true  },
  { label: 'totalCourierCharges',     key: 'totalCourierCharges',     currency: true  },
  { label: 'totalDropshippingCharges',key: 'totalDropshippingCharges',currency: true  },
  { label: 'totalServiceCharges',     key: 'totalServiceCharges',     currency: true  },
  { label: 'totalReturnPenalties',    key: 'totalReturnPenalties',    currency: true  },
  { label: 'totalAmountPayable',      key: 'totalAmountPayable',      currency: true, highlight: true },
];

// ── PDF generator ────────────────────────────────────────────────────────────
const generatePDF = (invoice, t, formatCurrency) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const pageW   = doc.internal.pageSize.getWidth();   // 210
  const marginX = 12;
  const contentW = pageW - marginX * 2;               // 186

  const primaryColor  = [79, 70, 229];   // indigo-ish
  const successColor  = [5, 150, 105];
  const warningColor  = [217, 119, 6];
  const grayDark      = [30, 30, 30];
  const grayMid       = [90, 90, 90];
  const grayLight     = [200, 200, 200];
  const grayBg        = [245, 245, 250];

  // ── 1. Header banner ──────────────────────────────────────────────────
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageW, 28, 'F');

  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('DROPSHIPPING INVOICE', marginX, 11);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(invoice.invoiceNumber, marginX, 18);

  // Status badge (top-right)
  const statusLabel = invoice.status?.toUpperCase() || 'GENERATED';
  const statusW = 28;
  const statusX = pageW - marginX - statusW;
  let badgeColor = [120, 113, 108];
  if (invoice.status === 'paid')     badgeColor = successColor;
  if (invoice.status === 'disputed') badgeColor = warningColor;
  doc.setFillColor(...badgeColor);
  doc.roundedRect(statusX, 10, statusW, 8, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(statusLabel, statusX + statusW / 2, 15.2, { align: 'center' });

  let y = 36;

  // ── 2. Meta row (period / generated date) ─────────────────────────────
  doc.setFillColor(...grayBg);
  doc.rect(marginX, y, contentW, 8, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayMid);

  const period = `${formatDate(invoice.periodFrom)} — ${formatDate(invoice.periodTo)}`;
  const genDate = `${t('dropshipping.generatedDate')}: ${formatDate(invoice.generatedAt)}`;
  doc.text(`${t('dropshipping.period')}: ${period}`, marginX + 4, y + 5.2);
  doc.text(genDate, pageW - marginX - 4, y + 5.2, { align: 'right' });
  y += 14;

  // ── 3. Provider / Reseller info block ─────────────────────────────────
  const colW = contentW / 2 - 3;

  const drawInfoBox = (title, name, address, phone, x) => {
    doc.setDrawColor(...grayLight);
    doc.setLineWidth(0.3);
    doc.rect(x, y, colW, 26);
    doc.setFillColor(...primaryColor);
    doc.rect(x, y, colW, 6, 'F');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(title.toUpperCase(), x + 3, y + 4.2);

    doc.setTextColor(...grayDark);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text(name || '-', x + 3, y + 11);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...grayMid);
    if (address) doc.text(String(address), x + 3, y + 16.5, { maxWidth: colW - 6 });
    if (phone)   doc.text(String(phone),   x + 3, y + 21.5);
  };

  drawInfoBox(
    t('dropshipping.providerBusiness'),
    invoice.providerBusiness?.name,
    invoice.providerBusiness?.businessAddress,
    invoice.providerBusiness?.businessPhone,
    marginX
  );
  drawInfoBox(
    t('dropshipping.resellerBusiness'),
    invoice.resellerBusiness?.name,
    invoice.resellerBusiness?.businessAddress,
    invoice.resellerBusiness?.businessPhone,
    marginX + colW + 6
  );
  y += 32;

  // ── 4. Bank details (if available) ────────────────────────────────────
  const shop = invoice.shop;
  if (shop?.resellerAccountName || shop?.resellerAccountNumber) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text(t('dropshipping.bankDetails').toUpperCase(), marginX, y);
    y += 4;

    doc.setDrawColor(...grayLight);
    doc.setLineWidth(0.3);
    doc.rect(marginX, y, contentW, 16);

    const bankCols = [
      [t('dropshipping.shopName'),            shop?.name                            || '-'],
      [t('dropshipping.resellerAccountName'), shop?.resellerAccountName             || '-'],
      [t('dropshipping.resellerAccountNumber'),shop?.resellerAccountNumber          || '-'],
      [t('dropshipping.bankName'),            shop?.resellerBank?.name              || '-'],
      [t('dropshipping.branchName'),          shop?.resellerBankBranch?.branchName  || '-'],
    ];

    const cellW = contentW / bankCols.length;
    bankCols.forEach(([label, val], i) => {
      const cx = marginX + i * cellW;
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grayMid);
      doc.text(label, cx + 3, y + 5);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...grayDark);
      doc.text(val, cx + 3, y + 11, { maxWidth: cellW - 6 });
    });
    y += 22;
  }

  // ── 5. Summary table ──────────────────────────────────────────────────
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text(t('dropshipping.summaryTotals').toUpperCase(), marginX, y);
  y += 3;

  const summaryRows = SUMMARY_ITEMS.map((item) => {
    const raw = invoice[item.key];
    const val = item.currency ? formatCurrency(raw) : (raw ?? '-');
    return [t(`dropshipping.${item.label}`), val];
  });

  autoTable(doc, {
    startY: y,
    margin: { left: marginX, right: marginX },
    tableWidth: contentW,
    head: [],
    body: summaryRows,
    columnStyles: {
      0: { cellWidth: contentW * 0.62, fontStyle: 'normal', fontSize: 8, textColor: grayMid },
      1: { cellWidth: contentW * 0.38, halign: 'right',     fontSize: 8, fontStyle: 'bold', textColor: grayDark },
    },
    bodyStyles: { cellPadding: 2.5 },
    alternateRowStyles: { fillColor: grayBg },
    // Highlight last row (totalAmountPayable)
    didParseCell: (data) => {
      if (data.row.index === summaryRows.length - 1) {
        data.cell.styles.fillColor = primaryColor;
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize  = 8.5;
      }
    },
    theme: 'plain',
  });

  y = doc.lastAutoTable.finalY + 10;

  // ── 6. Line items table ───────────────────────────────────────────────
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text(t('dropshipping.lineItems').toUpperCase(), marginX, y);
  y += 3;

  const lineItems = invoice.invoiceOrders || invoice.DropshippingInvoiceOrders || [];

  // Column widths totalling 186mm
  // Airwaybill(38) + Status(18) + COD(22) + Courier(22) + Dropshipping(26) + Service(20) + Penalty(22) + Payable(18)
  const liHead = [[
    t('dropshipping.airwaybill'),
    t('dropshipping.deliveryStatus'),
    t('dropshipping.codAmount'),
    t('dropshipping.courierCharge'),
    t('dropshipping.dropshippingCharge'),
    t('dropshipping.serviceCharge'),
    t('dropshipping.returnPenalty'),
    t('dropshipping.amountPayable'),
  ]];

  const liBody = lineItems.map((item) => [
    item.airwaybillNumber || '-',
    item.deliveryStatus   || '-',
    fmt(item.codAmount),
    fmt(item.courierCharge),
    fmt(item.dropshippingCharge),
    fmt(item.serviceCharge),
    fmt(item.returnPenalty),
    fmt(item.amountPayable),
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: marginX, right: marginX },
    tableWidth: contentW,
    head: liHead,
    body: liBody,
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7,
      cellPadding: 2.5,
      halign: 'center',
    },
    bodyStyles: {
      fontSize: 7,
      cellPadding: 2,
    },
    alternateRowStyles: { fillColor: grayBg },
    columnStyles: {
      0: { cellWidth: 38, fontStyle: 'bold', textColor: primaryColor },
      1: { cellWidth: 18, halign: 'center' },
      2: { cellWidth: 22, halign: 'right' },
      3: { cellWidth: 22, halign: 'right' },
      4: { cellWidth: 26, halign: 'right' },
      5: { cellWidth: 20, halign: 'right' },
      6: { cellWidth: 22, halign: 'right' },
      7: { cellWidth: 18, halign: 'right', fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      // Colour delivery status
      if (data.section === 'body' && data.column.index === 1) {
        const status = lineItems[data.row.index]?.deliveryStatus;
        if (status === 'delivered') data.cell.styles.textColor = successColor;
        if (status === 'returned')  data.cell.styles.textColor = [220, 38, 38];
      }
    },
    theme: 'plain',
  });

  // ── 7. Footer ─────────────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayMid);
    const pageH = doc.internal.pageSize.getHeight();
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageW / 2, pageH - 8,
      { align: 'center' }
    );
    doc.text(
      invoice.invoiceNumber,
      marginX, pageH - 8
    );
  }

  doc.save(`${invoice.invoiceNumber}.pdf`);
};

// ── CSV export for line items ─────────────────────────────────────────────
const exportLineItemsCsv = (invoice, t) => {
  const headers = [
    t('dropshipping.airwaybill'),
    t('dropshipping.deliveryStatus'),
    t('dropshipping.codAmount'),
    t('dropshipping.courierCharge'),
    t('dropshipping.dropshippingCharge'),
    t('dropshipping.serviceCharge'),
    t('dropshipping.returnPenalty'),
    t('dropshipping.amountPayable'),
  ];
  const items = invoice.invoiceOrders || invoice.DropshippingInvoiceOrders || [];
  const rows = items.map((item) => [
    item.airwaybillNumber || '',
    item.deliveryStatus   || '',
    item.codAmount        ?? 0,
    item.courierCharge    ?? 0,
    item.dropshippingCharge ?? 0,
    item.serviceCharge    ?? 0,
    item.returnPenalty    ?? 0,
    item.amountPayable    ?? 0,
  ]);

  const csv = [
    headers.join(','),
    ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = window.URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `${invoice.invoiceNumber}-line-items.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
};

// ── Component ────────────────────────────────────────────────────────────────
const DropshippingInvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, formatCurrency } = useLocalization();
  const { error: showError } = useAlert();

  const [invoice, setInvoice]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [lineSearch, setLineSearch] = useState('');

  useEffect(() => {
    getDropshippingInvoiceById(id)
      .then(setInvoice)
      .catch((err) => showError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  const allLineItems = useMemo(
    () => invoice?.invoiceOrders || invoice?.DropshippingInvoiceOrders || [],
    [invoice]
  );

  const filteredLineItems = useMemo(() => {
    if (!lineSearch.trim()) return allLineItems;
    const q = lineSearch.trim().toLowerCase();
    return allLineItems.filter(
      (item) =>
        item.airwaybillNumber?.toLowerCase().includes(q) ||
        item.deliveryStatus?.toLowerCase().includes(q)
    );
  }, [allLineItems, lineSearch]);

  if (loading) return <div className={styles.loading}>{t('dropshipping.loadingInvoice')}</div>;
  if (!invoice) return <div className={styles.loading}>{t('dropshipping.failedToLoadInvoice')}</div>;

  return (
    <div className={styles.page}>
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className={styles.toolbar}>
        <button className={styles.backBtn} onClick={() => navigate('/dropshipping/invoices')}>
          <ArrowLeft size={16} />
          {t('dropshipping.backToInvoices')}
        </button>
        <div className={styles.toolbarActions}>
          <button
            className={styles.csvBtn}
            onClick={() => exportLineItemsCsv(invoice, t)}
            title={t('dropshipping.exportLineItemsCsv')}
          >
            <Download size={15} />
            {t('dropshipping.exportLineItemsCsv')}
          </button>
          <button
            className={styles.pdfBtn}
            onClick={() => generatePDF(invoice, t, formatCurrency)}
            title={t('dropshipping.downloadPdf')}
          >
            <FileDown size={15} />
            {t('dropshipping.downloadPdf')}
          </button>
        </div>
      </div>

      {/* ── Invoice header ───────────────────────────────────────────────── */}
      <div className={styles.invoiceHeader}>
        <div className={styles.invoiceHeaderLeft}>
          <h1 className={styles.invoiceNumber}>{invoice.invoiceNumber}</h1>
          <div className={styles.meta}>
            <span>
              {t('dropshipping.providerBusiness')}:{' '}
              <strong>{invoice.providerBusiness?.name || invoice.providerBusinessId}</strong>
            </span>
            <span>
              {t('dropshipping.resellerBusiness')}:{' '}
              <strong>{invoice.resellerBusiness?.name || invoice.resellerBusinessId}</strong>
            </span>
            <span>
              {t('dropshipping.period')}:{' '}
              <strong>
                {formatDate(invoice.periodFrom)} — {formatDate(invoice.periodTo)}
              </strong>
            </span>
            <span>
              {t('dropshipping.generatedDate')}:{' '}
              <strong>{formatDate(invoice.generatedAt)}</strong>
            </span>
          </div>
        </div>
        <span className={`${styles.statusBadge} ${styles[STATUS_CSS[invoice.status] || 'status_generated']}`}>
          {t(`dropshipping.${invoice.status}`)}
        </span>
      </div>

      {/* ── Summary table ───────────────────────────────────────────────── */}
      <div className={styles.summarySection}>
        <h2 className={styles.sectionTitle}>{t('dropshipping.summaryTotals')}</h2>
        <div className={styles.summaryTable}>
          {SUMMARY_ITEMS.map(({ label, key, currency, highlight }) => (
            <div
              key={key}
              className={`${styles.summaryRow} ${highlight ? styles.summaryRowHighlight : ''}`}
            >
              <span className={styles.summaryLabel}>{t(`dropshipping.${label}`)}</span>
              <span className={styles.summaryValue}>
                {currency ? formatCurrency(invoice[key]) : (invoice[key] ?? '-')}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Line items ──────────────────────────────────────────────────── */}
      <div className={styles.lineItemsSection}>
        <div className={styles.lineItemsHeader}>
          <h2 className={styles.sectionTitle}>{t('dropshipping.lineItems')}</h2>
          <div className={styles.lineItemsControls}>
            <div className={styles.searchBox}>
              <Search size={14} className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                value={lineSearch}
                onChange={(e) => setLineSearch(e.target.value)}
                placeholder={t('dropshipping.searchLineItems')}
              />
              {lineSearch && (
                <button className={styles.clearSearch} onClick={() => setLineSearch('')}>
                  <X size={13} />
                </button>
              )}
            </div>
            <span className={styles.lineItemCount}>
              {filteredLineItems.length} / {allLineItems.length}
            </span>
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('dropshipping.airwaybill')}</th>
                <th>{t('dropshipping.deliveryStatus')}</th>
                <th className={styles.numCol}>{t('dropshipping.codAmount')}</th>
                <th className={styles.numCol}>{t('dropshipping.courierCharge')}</th>
                <th className={styles.numCol}>{t('dropshipping.dropshippingCharge')}</th>
                <th className={styles.numCol}>{t('dropshipping.serviceCharge')}</th>
                <th className={styles.numCol}>{t('dropshipping.returnPenalty')}</th>
                <th className={styles.numCol}>{t('dropshipping.amountPayable')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredLineItems.length === 0 ? (
                <tr>
                  <td colSpan={8} className={styles.emptyRow}>
                    {lineSearch ? t('dropshipping.noLineItemsFound') : t('dropshipping.noLineItems')}
                  </td>
                </tr>
              ) : (
                filteredLineItems.map((item) => (
                  <tr key={item.id}>
                    <td className={styles.airwaybill}>{item.airwaybillNumber}</td>
                    <td>
                      <span
                        className={`${styles.deliveryBadge} ${
                          item.deliveryStatus === 'delivered' ? styles.delivered : styles.returned
                        }`}
                      >
                        {t(`dropshipping.${item.deliveryStatus}`)}
                      </span>
                    </td>
                    <td className={styles.numCol}>{formatCurrency(item.codAmount)}</td>
                    <td className={styles.numCol}>{formatCurrency(item.courierCharge)}</td>
                    <td className={styles.numCol}>{formatCurrency(item.dropshippingCharge)}</td>
                    <td className={styles.numCol}>{formatCurrency(item.serviceCharge)}</td>
                    <td className={styles.numCol}>{formatCurrency(item.returnPenalty)}</td>
                    <td className={`${styles.numCol} ${styles.payable}`}>
                      {formatCurrency(item.amountPayable)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DropshippingInvoiceDetailPage;
